/**
 * BookingDiagnostics — in-app email/notifier verification kit.
 * Lets an operator confirm which notifier version is running, what the
 * env fingerprint looks like, send a real Resend test email, simulate
 * the exact booking-notification code path, and review the last N
 * booking_notifications logs — all without SSH.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { RefreshCw, Send, Beaker, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;

const StatusBadge = ({ status }) => {
  const s = status || 'unknown';
  const green = ['sent'];
  const amber = ['sent_admin_only', 'sent_client_only'];
  const red = ['failed', 'no_provider', 'no_recipients', 'queued'];
  let cls = 'bg-slate-100 text-slate-700';
  if (green.includes(s)) cls = 'bg-emerald-100 text-emerald-800';
  else if (amber.includes(s)) cls = 'bg-amber-100 text-amber-800';
  else if (red.includes(s)) cls = 'bg-rose-100 text-rose-800';
  return <Badge className={`${cls} border-0`} data-testid={`notif-status-${s}`}>{s}</Badge>;
};

export default function BookingDiagnostics() {
  const auth = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
  const [diag, setDiag] = useState(null);
  const [notifs, setNotifs] = useState([]);
  const [testEmail, setTestEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const loadDiag = async () => {
    setLoading(true);
    try {
      const [d, n] = await Promise.all([
        axios.get(`${API}/api/booking/notify-diag`, auth),
        axios.get(`${API}/api/booking/notifications?limit=20`, auth),
      ]);
      setDiag(d.data);
      setNotifs(n.data || []);
    } catch (e) {
      toast.error('Failed to load diagnostics: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadDiag(); }, []);

  const sendPlainTest = async () => {
    const to = testEmail.trim();
    if (!to) { toast.error('Enter a recipient email first'); return; }
    setBusy(true); setLastResult(null);
    try {
      const { data } = await axios.post(`${API}/api/booking/notify-diag/send-test`, { to }, auth);
      setLastResult({ kind: 'plain', data });
      if (data.ok) toast.success(`Resend accepted — id ${data.resend_response?.id || ''}`);
      else toast.error(`Send failed: ${data.error || data.reason || 'unknown'}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const simulateBooking = async () => {
    const to = testEmail.trim();
    if (!to) { toast.error('Enter a recipient email first'); return; }
    setBusy(true); setLastResult(null);
    try {
      const { data } = await axios.post(`${API}/api/booking/notify-diag/simulate`, { to }, auth);
      setLastResult({ kind: 'sim', data });
      if (data.ok && data.log?.status === 'sent') toast.success('Full notifier path succeeded');
      else if (data.ok) toast.warning(`Notifier ran — status "${data.log?.status}"`);
      else toast.error(`Simulate failed: ${data.error || 'unknown'}`);
      await loadDiag();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const good = diag?.resend_module_available && diag?.sender_email && diag?.resend_api_key_present;

  return (
    <div className="space-y-6" data-testid="booking-diagnostics">
      {/* Env header */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="text-base sm:text-lg">Notifier health</CardTitle>
              <p className="text-xs text-slate-500 mt-1">
                Confirms the exact code your backend is running. If prod shows an older <code>notifier_version</code>, your last deploy didn&apos;t reload the backend.
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={loadDiag} disabled={loading} data-testid="diag-refresh-btn">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline ml-2">Refresh</span>
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div className="flex items-center gap-2">
              {good ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <XCircle className="h-4 w-4 text-rose-600" />}
              <span className="text-slate-600">Notifier version</span>
              <code className="ml-auto text-xs bg-slate-100 px-2 py-0.5 rounded" data-testid="diag-version">{diag?.notifier_version || '—'}</code>
            </div>
            <div className="flex items-center gap-2">
              {diag?.resend_module_available ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <XCircle className="h-4 w-4 text-rose-600" />}
              <span className="text-slate-600">resend module</span>
              <span className="ml-auto font-mono text-xs">{String(diag?.resend_module_available ?? '—')}</span>
            </div>
            <div className="flex items-center gap-2">
              {diag?.sender_email ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <XCircle className="h-4 w-4 text-rose-600" />}
              <span className="text-slate-600">sender email</span>
              <code className="ml-auto text-xs bg-slate-100 px-2 py-0.5 rounded" data-testid="diag-sender">{diag?.sender_email || '(missing)'}</code>
            </div>
            <div className="flex items-center gap-2">
              {diag?.resend_api_key_present ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <XCircle className="h-4 w-4 text-rose-600" />}
              <span className="text-slate-600">RESEND_API_KEY</span>
              <code className="ml-auto text-xs bg-slate-100 px-2 py-0.5 rounded">{diag?.resend_api_key_prefix || '(missing)'}</code>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Test kit */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base sm:text-lg">Send a test email</CardTitle>
          <p className="text-xs text-slate-500">
            <strong>Send test</strong> = raw Resend send (proves the provider works). <strong>Simulate booking</strong> = runs the EXACT code path a real booking triggers, then shows you the resulting notification log.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label htmlFor="diag-to" className="text-xs">Recipient email</Label>
            <Input
              id="diag-to"
              type="email"
              placeholder="you@example.com"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              data-testid="diag-to-input"
            />
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button onClick={sendPlainTest} disabled={busy || !good} className="flex-1" data-testid="diag-send-plain-btn">
              <Send className="h-4 w-4 mr-2" /> Send test
            </Button>
            <Button variant="secondary" onClick={simulateBooking} disabled={busy} className="flex-1" data-testid="diag-simulate-btn">
              <Beaker className="h-4 w-4 mr-2" /> Simulate booking
            </Button>
          </div>
          {!good && (
            <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>Resend is not fully configured. Check the &quot;Notifier health&quot; panel above — the missing item is the blocker.</span>
            </div>
          )}
          {lastResult && (
            <div className="text-xs bg-slate-50 border border-slate-200 rounded p-3 overflow-x-auto" data-testid="diag-last-result">
              <div className="font-medium mb-1 text-slate-600">Last result ({lastResult.kind === 'plain' ? 'Send test' : 'Simulate booking'})</div>
              <pre className="text-[11px] leading-snug">{JSON.stringify(lastResult.data, null, 2)}</pre>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent notifications */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="text-base sm:text-lg">Recent notifications</CardTitle>
              <p className="text-xs text-slate-500">Every notification attempt writes ONE row here with its status and any error.</p>
            </div>
            <Badge variant="outline" className="shrink-0">{notifs.length}</Badge>
          </div>
        </CardHeader>
        <CardContent>
          {notifs.length === 0 ? (
            <p className="text-sm text-slate-500 italic">No notifications yet. Simulate a booking above to create one.</p>
          ) : (
            <div className="space-y-2">
              {notifs.map((n) => (
                <div key={n.id} className="border rounded-lg p-3 text-xs" data-testid={`notif-row-${n.id}`}>
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <StatusBadge status={n.status} />
                    <span className="text-slate-500">{n.kind}</span>
                    <span className="text-slate-400">·</span>
                    <span className="text-slate-500">{n.created_at ? new Date(n.created_at).toLocaleString() : ''}</span>
                    {n.notifier_version && (
                      <code className="ml-auto text-[10px] bg-slate-100 px-1.5 py-0.5 rounded">{n.notifier_version}</code>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-slate-700">
                    <div><span className="text-slate-400">to:</span> {n.to || <em className="text-slate-400">(none)</em>}</div>
                    <div><span className="text-slate-400">admin cc:</span> {n.cc_admin || <em className="text-slate-400">(none)</em>}</div>
                    <div><span className="text-slate-400">subject:</span> {n.subject}</div>
                    <div><span className="text-slate-400">resend_id:</span> {n.resend_id || <em className="text-slate-400">—</em>}</div>
                  </div>
                  {n.error && (
                    <div className="mt-2 text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">
                      <span className="font-medium">error:</span> {n.error}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
